import { Config } from "~/constants/configuration_provider";
import { selectUser, users } from "./development-provider";

// FeatureName type ensures the correct feature name
// identifiers are used everywhere. Also allows autocompletion.
type FeatureName =
  | "Agent Info"
  | "Developer Tools"
  | "Feature Flags"
  | "Finding status tags"
  | "Foundation themes"
  | "Hosted AWS - Role Auth"
  | "Mondoo BI Demo"
  | "Move the needle"
  | "MTN Initiatives"
  | "Multi-finding ticketing"
  | "New Org Dashboard"
  | "Platform Policy Management"
  | "Platform Policy Exceptions"
  | "Platform Policy Add"
  | "Organization Policy Management"
  | "Organization Policy Add"
  | "Reporting"
  | "Security Pipeline"
  | "ServiceNow Integration"
  | "Slack Start Convo"
  | "User Management"
  | "Sprint Streak";

export interface FeatureFlag {
  name: FeatureName;
  enabled: boolean;
  description?: string;
  showInDrawer?: boolean;
  headerKey?: string; // if feature has corresponding X-MONDOO-FEATURE-FLAGS value
  isUserModified?: boolean; // tracks if user explicitly modified this feature
}

export const features: FeatureFlag[] = [
  {
    name: "Feature Flags",
    enabled: false,
    description: "Enable the Feature Flags button",
    showInDrawer: false,
  },
  {
    name: "Developer Tools",
    enabled: false,
    description: "Additional tools for debugging/discovering",
  },
  {
    name: "Hosted AWS - Role Auth",
    enabled: false,
    description:
      "Gain access to use the new Hosted AWS integration using Role Authentication.",
  },
  // We are purposely filtering Reporing out of the drawer and /flags page
  // as this is not ready for public consumption yet.
  {
    name: "Reporting",
    enabled: false,
    description:
      "Enable the generating PDF reports - requires refresh to take effect",
    showInDrawer: false,
  },
  {
    name: "Mondoo BI Demo",
    enabled: false,
    description: "A demo of Mondoo BI for RSA ONLY",
  },
  {
    name: "Slack Start Convo",
    enabled: false,
    description:
      "Enable the ability for a user to start a conversation in slack from triage",
  },
  {
    name: "Move the needle",
    enabled: false,
    description: "Feature to move the needle on Dashboard",
  },
  {
    name: "Sprint Streak",
    enabled: false,
    description: "Enables sprint streak chart on space/workspace dashboard",
  },
  {
    name: "Security Pipeline",
    enabled: false,
    description: "Fix forever security pipeline",
  },
  {
    name: "Platform Policy Management",
    enabled: false,
    description: "Enable ability to manage policies at the platform level",
  },
  {
    name: "Platform Policy Exceptions",
    enabled: false,
    description: "Enable exceptions and properties tabs for platform policies",
  },
  {
    name: "Platform Policy Add",
    enabled: false,
    description: "Enable ability to assign platform policies",
  },
  {
    name: "Organization Policy Management",
    enabled: false,
    description: "Enable ability to manage policies at the organization level",
  },
  {
    name: "Organization Policy Add",
    enabled: false,
    description: "Enable ability to assign organization policies",
  },
  {
    name: "Foundation themes",
    enabled: false,
    description:
      "Enable the use of the MUI-standard foundation themes (WARNING: not yet ready for use) ",
    showInDrawer: false,
  },
  {
    name: "ServiceNow Integration",
    enabled: false,
    description:
      "Enable the ServiceNow integration - requires refresh to take effect",
  },
  {
    name: "Multi-finding ticketing",
    enabled: false,
    description: "Build multiple findings into one single ticket",
  },
  {
    name: "MTN Initiatives",
    enabled: false,
    description: "Display initiatives in the MTN Top Actions table",
  },
  {
    name: "User Management",
    enabled: false,
    description:
      "Enable the new User Management page at the organization level",
  },
  {
    name: "Finding status tags",
    enabled: false,
    description: "Enable the new status tag indicators in the findings table",
  },
  {
    name: "Agent Info",
    enabled: false,
    description:
      "Display connected service accounts and assets on the agent details page",
  },
  {
    name: "New Org Dashboard",
    enabled: false,
    description:
      "Enable the modernized organization dashboard with aggregate score APIs",
  },
];

// utility function to check if a feature is enabled for use outside of React components
export const checkFeatureEnabled = (name: FeatureName): boolean => {
  const feature = features.find((f) => f.name === name);
  return feature ? feature.enabled : false;
};

export const storedFeatures = readFeatureFlags();

storedFeatures.forEach((sf) => {
  const feature = features.find((f) => f.name === sf.name);
  if (feature) {
    feature.enabled = sf.enabled;
    feature.isUserModified = sf.isUserModified;
  }
});

if (Config.VITE_DEFAULT_FEATURES) {
  Config.VITE_DEFAULT_FEATURES.split(",").forEach((name) => {
    const feature = features.find(
      (f) => f.name === name.trim() && f.isUserModified !== true,
    );
    if (feature) {
      feature.enabled = true;
    }
  });
  saveFeatureFlags(features);
}

// Only allow "showInDrawer" features from being toggled in the UI. Please use CLI to toggle these features if necessary.
export function getVisibleFeatureFlags(features: FeatureFlag[]) {
  return features
    .filter((f) => f.showInDrawer !== false)
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function readFeatureFlags() {
  try {
    return JSON.parse(
      localStorage.getItem("features") || "[]",
    ) as FeatureFlag[]; // TODO: add guard that verifies localstorage doesn't have malformed data
  } catch (error) {
    return [];
  }
}

export function saveFeatureFlags(features: FeatureFlag[]) {
  return localStorage.setItem("features", JSON.stringify(features));
}

export type SetFeatureOptions = { reload: boolean; isUserModified: boolean };

export const defaultSetFeatureOptions: SetFeatureOptions = {
  reload: false,
  isUserModified: true,
};

export function setFeatureFlag(
  name: FeatureName,
  enabled: boolean,
  options = defaultSetFeatureOptions,
) {
  const feature = features.find((f) => f.name === name);
  if (feature) {
    feature.enabled = enabled;
    feature.isUserModified = options.isUserModified;
    saveFeatureFlags(features);
    if (options.reload) location.reload();
  }
}

export function enableFeatureFlag(
  name: FeatureName,
  options = defaultSetFeatureOptions,
) {
  setFeatureFlag(name, true, options);
}

export function disableFeatureFlag(
  name: FeatureName,
  options = defaultSetFeatureOptions,
) {
  setFeatureFlag(name, false, options);
}

export function toggleFeatureFlag(
  name: FeatureName,
  options = defaultSetFeatureOptions,
) {
  setFeatureFlag(name, !checkFeatureEnabled(name), options);
}

// console user manager
export class CliManager {
  users() {
    console.table(users);
  }

  su(token: string) {
    const result = users.find((usr) => usr.token === token);
    if (result) {
      selectUser(result);
    } else {
      console.error(`User with token "${token}" not found`);
    }
  }

  features() {
    console.table(features);
  }

  enableFeature = enableFeatureFlag;

  disableFeature = disableFeatureFlag;

  toggleFeature = toggleFeatureFlag;

  setFeature = setFeatureFlag;
}

// register user manager globally
window.cli = new CliManager();
